import { Icon } from '@buggy/shared'
import { Dropdown, MenuProps } from 'antd'
import React, { FunctionComponent } from 'react'
import styled from 'styled-components'
import PlatformTabSelector from '@/components/kol/common/platform-tab-selector'
import useKolDetailRoute from '@/components/kol/hooks/use-kol-detail-route'
import { kolDetailPostListAdvancedFilterSchema } from '@/hooks/use-authorization/routes'
import { useIntl } from '@/i18n/hooks/use-intl'
import { ampli } from '@/utils/ampli'
import {
  convertPlatformShortcode,
  Platform,
  PlatformShortcode,
} from '@/utils/convert-platform'

const KOL_DETAIL_PLATFORM_SELECT_HEIGHT = 48

export interface PlatformData extends Platform {
  url: string
  follower?: number
}

interface PlatformSelectProps {
  isLoading: boolean
  kolId?: string
  platformData?: PlatformData
  platforms: PlatformData[]
  layout: 'tabs' | 'dropdown'
  isUpdating?: boolean
}

const PlatformSelect: FunctionComponent<PlatformSelectProps> = ({
  isLoading,
  kolId,
  platformData,
  platforms,
  layout,
  isUpdating = false,
}) => {
  const { formatMessage } = useIntl()
  const { searchParams, setSearchParams } = useKolDetailRoute()

  const handlePlatformChange = async (
    platformShortCode: PlatformShortcode,
  ): Promise<void> => {
    if (platformShortCode === platformData?.shortcode) {
      return
    }

    ampli.changePlatform({
      kolId,
      platform: convertPlatformShortcode(platformShortCode).title,
    })

    await setSearchParams({
      ...searchParams.data,
      ...Object.fromEntries(
        Object.entries(
          kolDetailPostListAdvancedFilterSchema.keyof().options,
        ).map(([, value]) => [value, undefined]),
      ),
      platform: platformShortCode,
      postListPlatform: platformShortCode,
    })
  }

  if (layout === 'dropdown') {
    const items: MenuProps['items'] = platforms.map((data) => {
      return {
        key: data.shortcode,
        label: (
          <DropdownLabel
            onClick={(): Promise<void> => handlePlatformChange(data.shortcode)}
          >
            {data.sharedIconName && (
              <Icon fontSize={20} name={data.sharedIconName} />
            )}
            {data.follower
              ? data.title
              : formatMessage({ id: 'kol:data_platform_no_dosclosure' })}
          </DropdownLabel>
        ),
      }
    })

    return (
      <Dropdown menu={{ items, style: { padding: 0 } }} trigger={['click']}>
        <DropdownTrigger>
          {platformData?.sharedIconName && (
            <Icon fontSize={20} name={platformData.sharedIconName} />
          )}
          <Icon fontSize={16} name='chevron-down' />
        </DropdownTrigger>
      </Dropdown>
    )
  }

  return (
    <Box>
      <TabsWrapper>
        <PlatformTabSelector
          activePlatformShortcode={platformData?.shortcode}
          handlePlatformChange={handlePlatformChange}
          isFetching={isLoading}
          isUpdating={isUpdating}
          platforms={platforms}
        />
      </TabsWrapper>
    </Box>
  )
}

const DropdownLabel = styled.button`
  cursor: pointer;
  display: flex;
  padding: 3px 0;
  min-width: 120px;
  border: none;
  background: unset;
  gap: 5px;

  div {
    color: ${({ theme }): string => theme.colors.text.secondary};
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: 22px;
  }
`

const DropdownTrigger = styled.button`
  cursor: pointer;
  border: none;
  background: ${({ theme }): string => theme.colors.background.white};
  display: flex;
  align-items: center;
  padding: 0 20px;
  gap: 5px;
`

const Box = styled.div`
  position: sticky;
  background: ${({ theme }): string => theme.colors.background.white};
`

const TabsWrapper = styled.div`
  height: ${KOL_DETAIL_PLATFORM_SELECT_HEIGHT}px;
  max-width: 1280px;
  padding: 0 40px;
  margin: 0 auto;
  display: flex;
  align-items: end;
`

export default PlatformSelect
